import { Link } from "react-router-dom";
import type { Article } from "@/types/news";
import { getCategoryName } from "@/utils/articles";

interface TopicSectionProps {
  title: string;
  description: string;
  articles: Article[];
}

export default function TopicSection({ title, description, articles }: TopicSectionProps) {
  const [lead, second, third] = articles;
  if (!lead) return null;
  const previewItems = [lead, second, third].filter((item): item is Article => Boolean(item));

  return (
    <section className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-0.5 rounded-md bg-accent-500 text-foreground-950 text-[11px] font-bold">
              头条专题
            </span>
            <span className="text-[11px] font-label tracking-wider text-foreground-500">
              SPECIAL REPORT
            </span>
          </div>
          <h2 className="font-heading text-lg md:text-2xl font-black text-foreground-950 leading-snug">
            {title}
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-foreground-600 line-clamp-2 max-w-2xl">
            {description}
          </p>
        </div>
        <Link
          to={`/detail/${lead.id}`}
          className="hidden sm:inline-flex shrink-0 items-center gap-1 h-9 px-3.5 rounded-md bg-primary-600 text-background-50 text-sm hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
        >
          阅读专题
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-line text-sm" aria-hidden="true" />
          </span>
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5 md:gap-3">
        {previewItems.map((article, itemIndex) => (
          <Link
            key={article.id}
            to={`/detail/${article.id}`}
            className={`${itemIndex === 0 ? "col-span-2 md:col-span-1" : "col-span-1"} group relative rounded-md overflow-hidden bg-background-300 cursor-pointer`}
          >
            <div className={`${itemIndex === 0 ? "h-[170px] md:h-[190px]" : "h-[110px] md:h-[190px]"} w-full`}>
              <img
                src={article.image}
                alt={`${article.title}配图`}
                title={`${article.title} - 51黑料网`}
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/25 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-2.5 md:p-3">
              <span className="text-[10px] text-accent-300 font-medium">
                {getCategoryName(article.category)}
              </span>
              <p className="mt-1 text-xs md:text-sm font-heading font-bold text-background-50 leading-snug line-clamp-2">
                {article.title}
              </p>
            </div>
          </Link>
        ))}
      </div>

      <ul className="mt-4 pt-4 border-t border-background-300/70 space-y-2.5">
        {articles.slice(0, 3).map((article, itemIndex) => (
          <li key={article.id}>
            <Link
              to={`/detail/${article.id}`}
              className="group flex items-start gap-2.5 text-sm text-foreground-800 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <span className="mt-0.5 shrink-0 w-4 h-4 flex items-center justify-center rounded-sm bg-primary-100 text-primary-700 text-[10px] font-label font-bold">
                {itemIndex + 1}
              </span>
              <span className="line-clamp-1">{article.title}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}